import React, { useState, useEffect } from "react";
import { useLang } from "../contexts/LanguageContext";
import locales from "../utils/locales";
import categoryOptions from "../utils/categoryOptions";
import ActivityNameAutocomplete from "./ActivityNameAutocomplete";

// Helper Functions
function calcDuration(start, end) {
  if (!start || !end) return "";
  const [sh, sm] = start.split(":").map(Number);
  const [eh, em] = end.split(":").map(Number);
  let diff = (eh * 60 + em) - (sh * 60 + sm);
  if (diff < 0) diff += 24 * 60; // overnight
  if (isNaN(diff)) return "";
  if (diff >= 60 && diff % 60 !== 0)
    return `${Math.floor(diff / 60)}h ${diff % 60}min`;
  if (diff >= 60)
    return `${Math.floor(diff / 60)}h`;
  return `${diff}min`;
}
function isValidDuration(str) {
  if (!str) return false;
  str = str.trim();
  return (
    /^\d+\s*(h|hr|hrs|ชั่วโมง)?$/i.test(str) ||
    /^\d+\s*(m|min|mins|นาที)?$/i.test(str) ||
    /^\d{1,2}:\d{2}\s*-\s*\d{1,2}:\d{2}$/.test(str)
  );
}
function isZeroDuration(start, end) {
  if (!start || !end) return false;
  const [sh, sm] = start.split(":").map(Number);
  const [eh, em] = end.split(":").map(Number);
  return sh === eh && sm === em;
}

export default function EditActivityModal({
  show,
  onClose,
  activity = {},
  onSave,
  userId,
}) {
  const { lang } = useLang();
  const t = locales[lang];
  //const WEEKDAYS = [t.mon, t.tue, t.wed, t.thu, t.fri, t.sat, t.sun];

  // State
  const [newActivity, setNewActivity] = useState("");
  const [newDuration, setNewDuration] = useState("");
  const [newNote, setNewNote] = useState("");
  const [newMood, setNewMood] = useState("");
  const [newCategory, setNewCategory] = useState("work");
  const [newSubCategory, setNewSubCategory] = useState("");
  const [newDate, setNewDate] = useState(new Date().toISOString().slice(0, 10));
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [error, setError] = useState("");
  // Recurring state
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurrenceType, setRecurrenceType] = useState("daily");
  const [recurrenceInterval, setRecurrenceInterval] = useState(1);
  const [recurrenceDays, setRecurrenceDays] = useState([]);
  const [recurrenceEndDate, setRecurrenceEndDate] = useState("");

  // Mood list mapping to locales
  const moods = [
    { emoji: "😊", label: t.mood_happy },
    { emoji: "😌", label: t.mood_calm },
    { emoji: "😐", label: t.mood_neutral },
    { emoji: "😔", label: t.mood_sad },
    { emoji: "😠", label: t.mood_angry },
  ];

  // Set state from activity
  useEffect(() => {
    setNewActivity(activity.activity || "");
    setNewDuration(activity.duration || "");
    setNewNote(activity.note || "");
    setNewMood(activity.mood || "");
    setNewCategory(activity.category || "work");
    setNewSubCategory(activity.sub_category || "");
    setNewDate(
      activity.activity_date
        ? activity.activity_date.slice(0, 10)
        : new Date().toISOString().slice(0, 10)
    );
    setStartTime(activity.start_time || "");
    setEndTime(activity.end_time || "");
    setError("");
    setIsRecurring(activity.is_recurring || false);
    setRecurrenceType(activity.recurrence_type || "daily");
    setRecurrenceInterval(activity.recurrence_interval || 1);
    setRecurrenceDays(activity.recurrence_days || []);
    setRecurrenceEndDate(activity.recurrence_end_date || "");
  }, [
    activity.activity,
    activity.duration,
    activity.note,
    activity.mood,
    activity.category,
    activity.sub_category,
    activity.activity_date,
    activity.start_time,
    activity.end_time,
    activity.is_recurring,
    activity.recurrence_type,
    activity.recurrence_interval,
    activity.recurrence_days,
    activity.recurrence_end_date,
    show
  ]);

  useEffect(() => {
    if (startTime && endTime) setNewDuration(calcDuration(startTime, endTime));
  }, [startTime, endTime]);

  if (!show) return null;

  const isOvernight = !!(startTime && endTime && endTime < startTime);

  // Subcategories (support both .subcategories and .subs)
  const selectedCat = categoryOptions.find(c => c.value === newCategory);
  const subcategories = selectedCat?.subcategories || selectedCat?.subs || [];

  // --- Submit Handler ---
  const handleSubmit = (e) => {
    e.preventDefault();
    setError("");
    if (!newActivity.trim()) {
      setError(t.error_activity_name);
      return;
    }
    if (startTime && endTime && isZeroDuration(startTime, endTime)) {
      setError(t.error_start_end_same);
      return;
    }
    if (!startTime && !endTime && (!newDuration || !isValidDuration(newDuration))) {
      setError(t.error_duration);
      return;
    }
    if (isRecurring) {
      if (!recurrenceType) {
        setError(t.error_recur_type);
        return;
      }
      if (recurrenceType === "weekly" && recurrenceDays.length === 0) {
        setError(t.error_recur_day);
        return;
      }
      if (recurrenceEndDate && recurrenceEndDate < newDate) {
        setError(t.error_recur_end);
        return;
      }
    }
    const clean = (val) => (val === "" ? null : val);

    onSave({
      ...activity,
      activity: newActivity,
      duration: clean(newDuration),
      note: newNote,
      mood: newMood,
      category: newCategory,
      sub_category: newSubCategory || null,
      activity_date: newDate,
      start_time: clean(startTime),
      end_time: clean(endTime),
      is_recurring: isRecurring,
      recurrence_type: isRecurring ? recurrenceType : null,
      recurrence_interval: isRecurring ? recurrenceInterval : null,
      recurrence_days: isRecurring && recurrenceType === "weekly" ? recurrenceDays : null,
      recurrence_end_date: isRecurring ? clean(recurrenceEndDate) : null,
    });
  };

  //const toggleRecurrenceDay = (day) => {
  //  setRecurrenceDays((prev) =>
  //    prev.includes(day)
  //      ? prev.filter((d) => d !== day)
  //      : [...prev, day]
  //  );
  //};

  return (
    <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center">
      <div className="bg-white rounded-xl p-6 w-full max-w-sm shadow-lg">
        <h2 className="text-xl font-bold mb-4">
          {activity.id ? t.edit_activity : t.add_activity}
        </h2>
        <form onSubmit={handleSubmit}>
        <ActivityNameAutocomplete
          value={newActivity}
          onChange={setNewActivity}
          userId={userId}
          placeholder={t.activity_placeholder}
        />
          {/* Time range */}
          <div className="flex gap-2 mb-3">
            <input
              className="w-full border rounded-xl px-3 py-2"
              type="time"
              value={startTime}
              onChange={e => setStartTime(e.target.value)}
              placeholder={t.start_time}
            />
            <span className="self-center">–</span>
            <input
              className="w-full border rounded-xl px-3 py-2"
              type="time"
              value={endTime}
              onChange={e => setEndTime(e.target.value)}
              placeholder={t.end_time}
            />
          </div>
          {isOvernight && (
            <div className="text-xs text-amber-600 mb-2 font-medium">
              {lang === "th"
                ? "กิจกรรมนี้ข้ามคืน (ข้ามวัน)"
                : "This activity is overnight (crosses midnight)"}
            </div>
          )}
          <input
            className="w-full border rounded-xl px-3 py-2 mb-3"
            value={newDuration}
            onChange={e => setNewDuration(e.target.value)}
            placeholder={t.duration_placeholder}
            required={!startTime && !endTime}
            disabled={!!(startTime && endTime)}
          />

          {/* Mood Picker */}
          <div>
            <div className="mb-1 font-medium text-gray-700">{t.mood_prompt}</div>
            <div className="flex gap-2 mb-2">
              {moods.map(m => (
                <button
                  key={m.emoji}
                  type="button"
                  aria-label={m.label}
                  className={`text-2xl px-2 py-1 rounded-full border 
                    ${newMood === m.emoji ? "bg-blue-100 border-blue-400" : "border-gray-300"}`}
                  onClick={() => setNewMood(m.emoji)}
                >
                  {m.emoji}
                </button>
              ))}
            </div>
          </div>

          <textarea
            className="w-full border rounded-xl px-3 py-2 mb-3"
            value={newNote}
            onChange={e => setNewNote(e.target.value)}
            placeholder={t.note_placeholder}
            rows={3}
          />
          {/* Category Select */}
          <select
            className="w-full border rounded-xl px-3 py-2 mb-3"
            value={newCategory}
            onChange={e => {
              setNewCategory(e.target.value);
              setNewSubCategory(""); // Reset subcat เมื่อเปลี่ยน category
            }}
          >
            {categoryOptions.map(c => (
              <option key={c.value} value={c.value}>
                {typeof c.label === "object" ? c.label[lang] : c.label}
              </option>
            ))}
          </select>
          {/* Sub-category Select */}
          {subcategories.length > 0 && (
            <select
              className="w-full border rounded-xl px-3 py-2 mb-3"
              value={newSubCategory}
              onChange={e => setNewSubCategory(e.target.value)}
            >
              <option value="">
                {lang === "th" ? "สะท้อนอะไรกับใจ?" : "Pick a Life Aspect"}
              </option>
              {subcategories.map(sub => (
                <option key={sub.value} value={sub.value}>
                  {sub.label?.[lang] || sub.label}
                </option>
              ))}
            </select>
          )}

          <input
            type="date"
            className="w-full border rounded-xl px-3 py-2 mb-3"
            value={newDate}
            onChange={e => setNewDate(e.target.value)}
            required
          />

          {/* Recurring section */}
          {/*
          <div className="mb-2">
            <label className="flex items-center gap-2 mb-1 font-medium">
              <input
                type="checkbox"
                checked={isRecurring}
                onChange={e => setIsRecurring(e.target.checked)}
              />
              {t.repeat}
            </label>
            {isRecurring && (
              <div className="space-y-2 mt-2">
                <div className="flex gap-2 items-center">
                  <span>{t.repeat_every}</span>
                  <input
                    type="number"
                    min={1}
                    className="border rounded px-2 w-16"
                    value={recurrenceInterval}
                    onChange={e => setRecurrenceInterval(Number(e.target.value))}
                  />
                  <select
                    className="border rounded px-2"
                    value={recurrenceType}
                    onChange={e => setRecurrenceType(e.target.value)}
                  >
                    <option value="daily">{t.days}</option>
                    <option value="weekly">{t.weeks}</option>
                    <option value="monthly">{t.months}</option>
                  </select>
                </div>
                {recurrenceType === "weekly" && (
                  <div className="flex gap-1 flex-wrap">
                    {WEEKDAYS.map((day, i) => (
                      <label key={day} className={`border px-2 py-1 rounded cursor-pointer ${recurrenceDays.includes(day) ? "bg-indigo-500 text-white" : "bg-gray-100"}`}>
                        <input
                          type="checkbox"
                          checked={recurrenceDays.includes(day)}
                          onChange={() => toggleRecurrenceDay(day)}
                          className="mr-1"
                        />
                        {day}
                      </label>
                    ))}
                  </div>
                )}
                <div>
                  <label className="block text-sm mb-1">{t.recur_end_label}</label>
                  <input
                    type="date"
                    className="border rounded px-2 py-1 w-full"
                    value={recurrenceEndDate}
                    onChange={e => setRecurrenceEndDate(e.target.value)}
                  />
                </div>
              </div>
            )}
          </div>
          */}

          {error && (
            <div className="text-red-500 mb-2 text-sm">{error}</div>
          )}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl bg-gray-100 text-gray-700"
            >
              {t.cancel}
            </button>
            <button
              type="submit"
              className="px-4 py-2 rounded-xl bg-blue-500 text-white font-bold"
            >
              {t.save}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}